import React, { useCallback, useState } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  Button,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import { keyframes } from '@mui/system';
import CheckIcon from '@mui/icons-material/Check';
import { AspectRatio } from '@/pages/Record/Recorder/types';

// --- Keyframe animations (matching plugin) ---

const startButtonShimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const resumePulse = keyframes`
  0%, 100% {
    background-color: rgba(209, 165, 66, 0.1);
    box-shadow: 0 0 0 0 rgba(209, 165, 66, 0);
  }
  50% {
    background-color: rgba(209, 165, 66, 0.25);
    box-shadow: 0 0 8px 2px rgba(209, 165, 66, 0.3);
  }
`;

const timeLimitPulse = keyframes`
  0%, 100% { background-color: transparent; }
  50% { background-color: rgba(255, 193, 7, 0.25); }
`;

// --- Inline SVG icon components (from plugin tool-icons) ---

const PauseFilledSvg: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="8" fill="currentColor" />
    <rect x="5.5" y="5" width="1.5" height="6" rx="0.3" fill="white" />
    <rect x="9" y="5" width="1.5" height="6" rx="0.3" fill="white" />
  </svg>
);

const ResumeFilledSvg: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="8" fill="currentColor" />
    <path d="M6.5 5.5 L11 8 L6.5 10.5 Z" fill="white" />
  </svg>
);

const DiscardSvg: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} fill="none" viewBox="0 0 16 16">
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M2 4.586h11.52m-8.319 0V3.293a1.3 1.3 0 0 1 .375-.914c.24-.243.566-.379.905-.379h2.56c.34 0 .665.136.905.379a1.3 1.3 0 0 1 .375.914v1.293m1.92 0v9.05a1.3 1.3 0 0 1-.375.915c-.24.242-.565.378-.905.378h-6.4c-.34 0-.665-.136-.905-.378a1.3 1.3 0 0 1-.375-.915v-9.05h8.96ZM6.48 7.818v3.88m2.563-3.88v3.88"
    />
  </svg>
);

const CameraSvg: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} fill="none" viewBox="0 0 16 16">
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15 4.818 10.547 8 15 11.181V4.818ZM9.275 3.547H2.276c-.702 0-1.272.57-1.272 1.272v6.362c0 .703.57 1.273 1.272 1.273h6.999c.703 0 1.272-.57 1.272-1.273V4.82c0-.702-.57-1.272-1.272-1.272Z"
    />
  </svg>
);

const MicSvg: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} fill="none" viewBox="0 0 16 16">
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M8.334 1.334c-.505 0-.99.192-1.346.533a1.777 1.777 0 0 0-.558 1.285v4.849c0 .482.2.944.558 1.285a1.95 1.95 0 0 0 1.346.533c.506 0 .99-.192 1.347-.533.358-.34.558-.803.558-1.285V3.152c0-.482-.2-.945-.558-1.285a1.952 1.952 0 0 0-1.347-.533v0Z"
    />
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M12.78 6.787V8a4.147 4.147 0 0 1-1.302 3 4.554 4.554 0 0 1-3.143 1.243A4.554 4.554 0 0 1 5.192 11a4.147 4.147 0 0 1-1.301-3V6.787m4.441 5.455v2.424m-2.539 0h5.08"
    />
  </svg>
);

const MicOffSvg: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} fill="none" viewBox="0 0 16 16">
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M8.334 1.334c-.505 0-.99.192-1.346.533a1.777 1.777 0 0 0-.558 1.285v4.849c0 .482.2.944.558 1.285a1.95 1.95 0 0 0 1.346.533c.506 0 .99-.192 1.347-.533.358-.34.558-.803.558-1.285V3.152c0-.482-.2-.945-.558-1.285a1.952 1.952 0 0 0-1.347-.533v0Z"
    />
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M12.78 6.787V8a4.147 4.147 0 0 1-1.302 3 4.554 4.554 0 0 1-3.143 1.243A4.554 4.554 0 0 1 5.192 11a4.147 4.147 0 0 1-1.301-3V6.787m4.441 5.455v2.424m-2.539 0h5.08"
    />
    <line x1="2" y1="2" x2="14" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const RestartSvg: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 -960 960 960" fill="currentColor">
    <path d="M451-122q-123-10-207-101t-84-216q0-77 35.5-145T295-695l43 43q-56 33-87 90.5T220-439q0 100 66 173t165 84v60Zm60 0v-60q100-12 165-84.5T741-439q0-109-75.5-184.5T481-699h-20l60 60-43 43-133-133 133-133 43 43-60 60h20q134 0 227 93.5T801-439q0 125-83.5 216T511-122Z" />
  </svg>
);

// --- Desktop toolbar design tokens ---

const TOOLBAR_STYLES = {
  container: {
    height: 48,
    borderRadius: '30px',
    backgroundColor: 'background.paper',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.15)',
    padding: '0 10px',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  recordingControlsGroup: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: '#f6f7fb',
    borderRadius: '30px',
    fontFamily: "'Satoshi', sans-serif",
    height: 'calc(100% - 12px)',
    paddingLeft: '2px',
    paddingRight: '2px',
    overflow: 'visible',
  },
  startButton: {
    minWidth: 'unset',
    width: 38,
    height: 38,
    padding: 0,
    borderRadius: '50%',
    position: 'relative',
    overflow: 'hidden',
    zIndex: 10,
    marginLeft: '4px',
    marginRight: '2px',
    flexShrink: 0,
    background:
      'linear-gradient(135deg, rgba(48, 128, 248, 0.95) 0%, rgba(70, 150, 255, 0.9) 50%, rgba(48, 128, 248, 0.95) 100%)',
    color: '#FFFFFF',
    fontFamily: "'Satoshi', sans-serif",
    fontWeight: 700,
    fontSize: '9px',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    lineHeight: 1,
    boxShadow:
      '0 2px 8px rgba(48, 128, 248, 0.4), 0 4px 16px rgba(48, 128, 248, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
    transition: 'transform 0.15s ease-in-out, box-shadow 0.2s ease-in-out',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      background:
        'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 25%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.1) 75%, transparent 100%)',
      backgroundSize: '200% 100%',
      animation: `${startButtonShimmer} 4s ease-in-out infinite`,
      pointerEvents: 'none',
    },
    '&:hover': {
      background:
        'linear-gradient(135deg, rgba(48, 128, 248, 0.95) 0%, rgba(70, 150, 255, 0.9) 50%, rgba(48, 128, 248, 0.95) 100%)',
      transform: 'scale(1.08)',
      boxShadow:
        '0 4px 12px rgba(48, 128, 248, 0.5), 0 6px 24px rgba(48, 128, 248, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
      '&::before': {
        animation: `${startButtonShimmer} 2.5s ease-in-out infinite`,
      },
    },
    '&:active': {
      transform: 'scale(0.95)',
      boxShadow: '0 1px 4px rgba(48, 128, 248, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
    },
    '&.Mui-disabled': {
      background: 'linear-gradient(135deg, #D4D4D4 0%, #A3A3A3 100%)',
      color: '#FFFFFF',
      boxShadow: 'none',
    },
  },
  doneButton: {
    minWidth: 'unset',
    width: 38,
    height: 38,
    padding: 0,
    borderRadius: '50%',
    position: 'relative',
    overflow: 'hidden',
    zIndex: 10,
    marginLeft: '4px',
    marginRight: '2px',
    flexShrink: 0,
    background:
      'linear-gradient(135deg, rgba(40, 167, 69, 0.95) 0%, rgba(60, 180, 90, 0.9) 50%, rgba(40, 167, 69, 0.95) 100%)',
    color: '#FFFFFF',
    fontFamily: "'Satoshi', sans-serif",
    fontWeight: 700,
    fontSize: '9px',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    lineHeight: 1,
    boxShadow:
      '0 2px 8px rgba(40, 167, 69, 0.4), 0 4px 16px rgba(40, 167, 69, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
    transition: 'transform 0.15s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
      background:
        'linear-gradient(135deg, rgba(40, 167, 69, 0.95) 0%, rgba(60, 180, 90, 0.9) 50%, rgba(40, 167, 69, 0.95) 100%)',
      transform: 'scale(1.08)',
      boxShadow:
        '0 4px 12px rgba(40, 167, 69, 0.5), 0 6px 24px rgba(40, 167, 69, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
    },
    '&:active': {
      transform: 'scale(0.95)',
      boxShadow: '0 1px 4px rgba(40, 167, 69, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
    },
    '&.Mui-disabled': {
      background: 'linear-gradient(135deg, #D4D4D4 0%, #A3A3A3 100%)',
      color: '#FFFFFF',
      boxShadow: 'none',
    },
  },
  iconButton: {
    width: 32,
    height: 32,
    color: '#9797a4',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(124, 139, 165, 0.1)',
      color: '#6e7684',
    },
    '&:active': {
      transform: 'scale(0.95)',
    },
    '&.Mui-disabled': {
      color: '#d4d4d4',
    },
  },
  pauseButton: {
    width: 32,
    height: 32,
    color: '#d1a542',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(124, 139, 165, 0.1)',
    },
    '&:active': {
      transform: 'scale(0.95)',
    },
    '&.Mui-disabled': {
      color: '#d1a542',
      opacity: 0.5,
    },
  },
  resumeButton: {
    width: 32,
    height: 32,
    color: '#d1a542',
    backgroundColor: 'rgba(209, 165, 66, 0.15)',
    borderRadius: '50%',
    animation: `${resumePulse} 1.5s ease-in-out infinite`,
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(209, 165, 66, 0.25)',
      animation: 'none',
    },
    '&:active': {
      transform: 'scale(0.95)',
    },
    '&.Mui-disabled': {
      color: '#d4d4d4',
      animation: 'none',
    },
  },
  timer: {
    fontFamily: "'Satoshi-Medium', sans-serif",
    fontWeight: 400,
    fontSize: '16px',
    letterSpacing: 0,
    color: '#29292f',
    minWidth: 50,
    textAlign: 'center' as const,
    padding: '4px 10px',
    borderRadius: '12px',
    userSelect: 'none' as const,
    transition: 'background-color 0.3s ease-in-out',
  },
  timerDanger: {
    animation: `${timeLimitPulse} 1.5s ease-in-out infinite`,
  },
  separator: {
    width: '1px',
    height: 19,
    backgroundColor: '#e8e8e8',
    mx: 0.5,
  },
};

// Mobile toolbar tokens — rendered below the viewfinder
const MOBILE_TOOLBAR_STYLES = {
  container: {
    height: 48,
    borderRadius: '24px',
    backgroundColor: 'background.paper',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.15)',
    padding: '0 10px',
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    maxWidth: 'calc(100vw - 24px)',
  },
  startButton: {
    minWidth: 'unset',
    width: 40,
    height: 40,
    padding: 0,
    borderRadius: '50%',
    background:
      'linear-gradient(135deg, rgba(48, 128, 248, 0.95) 0%, rgba(70, 150, 255, 0.9) 50%, rgba(48, 128, 248, 0.95) 100%)',
    color: '#FFFFFF',
    fontWeight: 700,
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    lineHeight: 1,
    boxShadow:
      '0 2px 8px rgba(48, 128, 248, 0.4), 0 4px 16px rgba(48, 128, 248, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
    transition: 'transform 0.15s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
      background:
        'linear-gradient(135deg, rgba(48, 128, 248, 0.95) 0%, rgba(70, 150, 255, 0.9) 50%, rgba(48, 128, 248, 0.95) 100%)',
    },
    '&:active': {
      transform: 'scale(0.92)',
    },
    '&.Mui-disabled': {
      background: 'linear-gradient(135deg, #D4D4D4 0%, #A3A3A3 100%)',
      color: '#FFFFFF',
      boxShadow: 'none',
    },
  },
  doneButton: {
    minWidth: 'unset',
    width: 40,
    height: 40,
    padding: 0,
    borderRadius: '50%',
    background:
      'linear-gradient(135deg, rgba(40, 167, 69, 0.95) 0%, rgba(60, 180, 90, 0.9) 50%, rgba(40, 167, 69, 0.95) 100%)',
    color: '#FFFFFF',
    fontWeight: 700,
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    lineHeight: 1,
    boxShadow:
      '0 2px 8px rgba(40, 167, 69, 0.4), 0 4px 16px rgba(40, 167, 69, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
    transition: 'transform 0.15s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
      background:
        'linear-gradient(135deg, rgba(40, 167, 69, 0.95) 0%, rgba(60, 180, 90, 0.9) 50%, rgba(40, 167, 69, 0.95) 100%)',
    },
    '&:active': {
      transform: 'scale(0.92)',
    },
    '&.Mui-disabled': {
      background: 'linear-gradient(135deg, #D4D4D4 0%, #A3A3A3 100%)',
      color: '#FFFFFF',
      boxShadow: 'none',
    },
  },
  iconButton: {
    width: 34,
    height: 34,
    padding: '5px',
    boxSizing: 'content-box',
    color: '#9797a4',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(124, 139, 165, 0.1)',
      color: '#6e7684',
    },
    '&:active': {
      transform: 'scale(0.92)',
    },
    '&.Mui-disabled': {
      color: '#d4d4d4',
    },
  },
  timer: {
    fontFamily: "'Satoshi-Medium', sans-serif",
    fontWeight: 400,
    fontVariantNumeric: 'tabular-nums',
    fontSize: '0.8rem',
    color: 'text.secondary',
    minWidth: 42,
    textAlign: 'center',
  },
  separator: {
    width: '1px',
    height: 18,
    backgroundColor: '#e8e8e8',
    mx: 0.25,
  },
} as const;

interface RecordingControlsProps {
  isRecording: boolean;
  isPaused: boolean;
  elapsed: number;
  maxDuration: number;
  /**
   * @deprecated Unused — the aspect-ratio picker was removed from this toolbar.
   * Optional so callers with no aspect-ratio concept (screen/audio recorders)
   * don't have to invent a dummy value just to satisfy the type.
   */
  aspectRatio?: AspectRatio;
  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
  onReset: () => void;
  /** Called when user clicks "Restart" — resets and immediately re-starts recording */
  onRestart?: () => void;
  /** @deprecated Unused — see {@link aspectRatio}. */
  onAspectRatioChange?: (ratio: AspectRatio) => void;
  disabled?: boolean;
  /** When true, renders as a translucent overlay for mobile fullscreen mode */
  mobile?: boolean;
  /** Available cameras for the mobile toolbar camera picker */
  cameras?: MediaDeviceInfo[];
  /** Currently selected camera device ID */
  selectedCameraId?: string | null;
  /** Callback when user selects a camera from the mobile toolbar menu */
  onCameraSelect?: (deviceId: string) => void;
  /** Whether the camera is currently switching */
  isSwitchingCamera?: boolean;
  /** Whether the camera is actively capturing video */
  isCameraActive?: boolean;
  /** Available microphones for the mobile toolbar mic picker */
  microphones?: MediaDeviceInfo[];
  /** Currently selected microphone device ID */
  selectedMicId?: string | null;
  /** Callback when user selects a microphone from the mobile toolbar menu */
  onMicSelect?: (deviceId: string) => void;
  /** Whether the microphone is currently switching */
  isSwitchingMic?: boolean;
  /** Whether the microphone is actively capturing audio */
  isMicActive?: boolean;
}

const formatTime = (seconds: number): string => {
  if (!isFinite(seconds) || seconds < 0) {
    return '00:00';
  }
  const date = new Date(seconds * 1000);
  try {
    return date.toISOString().substring(14, 19);
  } catch {
    return '00:00';
  }
};

/** Strip hex USB device IDs like "(0fd9:0066)" from browser labels */
const cleanLabel = (label: string): string => label.replace(/\s*\([0-9a-f]{4}:[0-9a-f]{4}\)\s*$/i, '').trim();

export const RecordingControls: React.FC<RecordingControlsProps> = ({
  isRecording,
  isPaused,
  elapsed,
  maxDuration,

  aspectRatio: _aspectRatio,
  onStart,
  onPause,
  onResume,
  onStop,
  onReset,
  onRestart,

  onAspectRatioChange: _onAspectRatioChange,
  disabled = false,
  mobile = false,
  cameras,
  selectedCameraId,
  onCameraSelect,
  isSwitchingCamera = false,
  isCameraActive = false,
  microphones,
  selectedMicId,
  onMicSelect,
  isSwitchingMic = false,
  isMicActive = false,
}) => {
  const [cameraMenuAnchor, setCameraMenuAnchor] = useState<HTMLButtonElement | null>(null);
  const [micMenuAnchor, setMicMenuAnchor] = useState<HTMLButtonElement | null>(null);

  const handleCameraMenuOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setCameraMenuAnchor(event.currentTarget);
  }, []);

  const handleCameraMenuClose = useCallback(() => {
    setCameraMenuAnchor(null);
  }, []);

  const handleCameraItemClick = useCallback(
    (deviceId: string) => {
      onCameraSelect?.(deviceId);
      setCameraMenuAnchor(null);
    },
    [onCameraSelect],
  );

  const handleMicMenuOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setMicMenuAnchor(event.currentTarget);
  }, []);

  const handleMicMenuClose = useCallback(() => {
    setMicMenuAnchor(null);
  }, []);

  const handleMicItemClick = useCallback(
    (deviceId: string) => {
      onMicSelect?.(deviceId);
      setMicMenuAnchor(null);
    },
    [onMicSelect],
  );

  const getCameraLabel = useCallback((device: MediaDeviceInfo, index: number) => {
    if (device.label && device.label.trim().length > 0) {
      return cleanLabel(device.label);
    }
    return `Camera ${index + 1}`;
  }, []);

  const getMicLabel = useCallback((device: MediaDeviceInfo, index: number) => {
    if (device.label && device.label.trim().length > 0) {
      return cleanLabel(device.label);
    }
    return `Microphone ${index + 1}`;
  }, []);

  const formattedElapsed = formatTime(elapsed);
  const hasMultipleCameras = (cameras?.length ?? 0) > 1;
  const isCameraMenuOpen = Boolean(cameraMenuAnchor);
  const hasMultipleMics = (microphones?.length ?? 0) > 1;
  const isMicMenuOpen = Boolean(micMenuAnchor);
  const isApproachingLimit = maxDuration > 0 && elapsed >= maxDuration * 0.9;

  const styles = mobile ? MOBILE_TOOLBAR_STYLES : TOOLBAR_STYLES;

  // Mobile overlay toolbar — right-handed layout (thumb reaches START on right)
  if (mobile) {
    return (
      <>
        <Box sx={styles.container}>
          {/* Mic indicator — always visible; opens picker if multiple mics */}
          <IconButton
            onClick={hasMultipleMics ? handleMicMenuOpen : undefined}
            disabled={disabled || isSwitchingMic || (hasMultipleMics && isRecording)}
            aria-label={isMicActive ? 'microphone active' : 'microphone inactive'}
            size="small"
            disableRipple={!hasMultipleMics}
            sx={{
              ...styles.iconButton,
              color: isMicActive ? '#78c072' : '#9797a4',
              ...(!hasMultipleMics && {
                cursor: 'default',
                '&:hover': { backgroundColor: 'transparent', color: isMicActive ? '#78c072' : '#9797a4' },
              }),
              ...(hasMultipleMics && {
                '&:hover': {
                  ...((styles.iconButton as Record<string, unknown>)['&:hover'] ?? {}),
                  color: isMicActive ? '#65ad5f' : '#6e7684',
                },
              }),
            }}
          >
            {isSwitchingMic ? (
              <CircularProgress size={18} sx={{ color: '#9797a4' }} />
            ) : isMicActive ? (
              <MicSvg />
            ) : (
              <MicOffSvg />
            )}
          </IconButton>

          {/* Camera selector — only shown when cameras are available */}
          {(cameras?.length ?? 0) > 0 && (
            <>
              <IconButton
                onClick={hasMultipleCameras ? handleCameraMenuOpen : undefined}
                disabled={disabled || isSwitchingCamera || (hasMultipleCameras && isRecording)}
                aria-label="select camera"
                size="small"
                disableRipple={!hasMultipleCameras}
                sx={{
                  ...styles.iconButton,
                  color: isCameraActive ? '#78c072' : '#9797a4',
                  ...(!hasMultipleCameras && {
                    cursor: 'default',
                    '&:hover': { backgroundColor: 'transparent', color: isCameraActive ? '#78c072' : '#9797a4' },
                  }),
                }}
              >
                {isSwitchingCamera ? <CircularProgress size={18} sx={{ color: '#9797a4' }} /> : <CameraSvg />}
              </IconButton>
              <Box sx={styles.separator} />
            </>
          )}

          {/* Discard */}
          <IconButton
            onClick={onReset}
            disabled={!isRecording || disabled}
            aria-label="discard recording"
            size="small"
            sx={styles.iconButton}
          >
            <DiscardSvg />
          </IconButton>

          {/* Pause / Resume */}
          <IconButton
            onClick={isPaused ? onResume : onPause}
            disabled={!isRecording || disabled}
            aria-label={isPaused ? 'resume recording' : 'pause recording'}
            size="small"
            sx={styles.iconButton}
          >
            {isPaused ? <ResumeFilledSvg /> : <PauseFilledSvg />}
          </IconButton>

          {/* Timer */}
          <Typography sx={styles.timer}>{formattedElapsed}</Typography>

          <Box sx={styles.separator} />

          {/* START / DONE Button — far right for thumb reach */}
          {isRecording ? (
            <Button onClick={onStop} disabled={disabled} sx={styles.doneButton}>
              Done
            </Button>
          ) : (
            <Button onClick={onStart} disabled={disabled} sx={styles.startButton}>
              Start
            </Button>
          )}
        </Box>

        {/* Camera popover menu */}
        <Popover
          open={isCameraMenuOpen}
          anchorEl={cameraMenuAnchor}
          onClose={handleCameraMenuClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          slotProps={{
            paper: {
              sx: {
                borderRadius: '15px',
                boxShadow: '0px 4px 20px 0px rgba(129, 129, 129, 0.12)',
                mt: -1,
                minWidth: 220,
                maxWidth: 'calc(100vw - 32px)',
                maxHeight: 280,
                overflow: 'auto',
              },
            },
          }}
        >
          <List disablePadding sx={{ py: 0.5 }}>
            {cameras?.map((device, index) => {
              const label = getCameraLabel(device, index);
              const isSelected = device.deviceId === selectedCameraId;
              return (
                <ListItemButton
                  key={device.deviceId || index}
                  onClick={() => handleCameraItemClick(device.deviceId)}
                  selected={isSelected}
                  sx={{
                    py: 1,
                    px: 2,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(43, 159, 255, 0.08)',
                    },
                    '&.Mui-selected:hover': {
                      backgroundColor: 'rgba(43, 159, 255, 0.12)',
                    },
                  }}
                >
                  <ListItemText
                    primary={label}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: isSelected ? 500 : 400,
                      color: isSelected ? 'primary.main' : 'text.primary',
                      noWrap: true,
                    }}
                  />
                  {isSelected && <CheckIcon sx={{ fontSize: 18, color: 'primary.main', ml: 1 }} />}
                </ListItemButton>
              );
            })}
          </List>
        </Popover>

        {/* Microphone popover menu */}
        <Popover
          open={isMicMenuOpen}
          anchorEl={micMenuAnchor}
          onClose={handleMicMenuClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          slotProps={{
            paper: {
              sx: {
                borderRadius: '15px',
                boxShadow: '0px 4px 20px 0px rgba(129, 129, 129, 0.12)',
                mt: -1,
                minWidth: 220,
                maxWidth: 'calc(100vw - 32px)',
                maxHeight: 280,
                overflow: 'auto',
              },
            },
          }}
        >
          <List disablePadding sx={{ py: 0.5 }}>
            {microphones?.map((device, index) => {
              const label = getMicLabel(device, index);
              const isSelected = device.deviceId === selectedMicId;
              return (
                <ListItemButton
                  key={device.deviceId || index}
                  onClick={() => handleMicItemClick(device.deviceId)}
                  selected={isSelected}
                  sx={{
                    py: 1,
                    px: 2,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(43, 159, 255, 0.08)',
                    },
                    '&.Mui-selected:hover': {
                      backgroundColor: 'rgba(43, 159, 255, 0.12)',
                    },
                  }}
                >
                  <ListItemText
                    primary={label}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: isSelected ? 500 : 400,
                      color: isSelected ? 'primary.main' : 'text.primary',
                      noWrap: true,
                    }}
                  />
                  {isSelected && <CheckIcon sx={{ fontSize: 18, color: 'primary.main', ml: 1 }} />}
                </ListItemButton>
              );
            })}
          </List>
        </Popover>
      </>
    );
  }

  // Desktop toolbar — matching Chrome plugin layout
  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
        {/* Toolbar Container */}
        <Box sx={TOOLBAR_STYLES.container}>
          {/* Recording Controls Group — gray background pill */}
          <Box sx={TOOLBAR_STYLES.recordingControlsGroup}>
            {/* START / DONE Button */}
            {isRecording ? (
              <Tooltip title="Finish Recording">
                <Button onClick={onStop} disabled={disabled} sx={TOOLBAR_STYLES.doneButton}>
                  Done
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title="Start Recording">
                <Button onClick={onStart} disabled={disabled} sx={TOOLBAR_STYLES.startButton}>
                  Start
                </Button>
              </Tooltip>
            )}

            {/* Timer */}
            <Typography
              sx={{
                ...TOOLBAR_STYLES.timer,
                ...(isApproachingLimit && TOOLBAR_STYLES.timerDanger),
              }}
            >
              {formattedElapsed}
            </Typography>

            {/* Pause / Resume Button */}
            {isPaused ? (
              <Tooltip title="Resume">
                <span>
                  <IconButton
                    onClick={onResume}
                    disabled={!isRecording || disabled}
                    aria-label="resume recording"
                    size="small"
                    sx={TOOLBAR_STYLES.resumeButton}
                  >
                    <ResumeFilledSvg />
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <Tooltip title="Pause">
                <span>
                  <IconButton
                    onClick={onPause}
                    disabled={!isRecording || disabled}
                    aria-label="pause recording"
                    size="small"
                    sx={TOOLBAR_STYLES.pauseButton}
                  >
                    <PauseFilledSvg />
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {/* Restart Button */}
            <Tooltip title="Restart">
              <span>
                <IconButton
                  onClick={onRestart ?? onReset}
                  disabled={!isRecording || disabled}
                  aria-label="restart recording"
                  size="small"
                  sx={TOOLBAR_STYLES.iconButton}
                >
                  <RestartSvg />
                </IconButton>
              </span>
            </Tooltip>

            {/* Discard Button */}
            <Tooltip title="Discard">
              <span>
                <IconButton
                  onClick={onReset}
                  disabled={!isRecording || disabled}
                  aria-label="discard recording"
                  size="small"
                  sx={TOOLBAR_STYLES.iconButton}
                >
                  <DiscardSvg />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {/* Device selectors — camera & mic */}
          <Box sx={TOOLBAR_STYLES.separator} />

          <Tooltip
            title={hasMultipleMics ? 'Select Microphone' : isMicActive ? 'Microphone Active' : 'Microphone Inactive'}
          >
            <span>
              <IconButton
                onClick={hasMultipleMics ? handleMicMenuOpen : undefined}
                disabled={disabled || isSwitchingMic || (hasMultipleMics && isRecording)}
                aria-label={isMicActive ? 'microphone active' : 'microphone inactive'}
                size="small"
                disableRipple={!hasMultipleMics}
                sx={{
                  ...TOOLBAR_STYLES.iconButton,
                  color: isMicActive ? '#78c072' : '#9797a4',
                  ...(!hasMultipleMics && {
                    cursor: 'default',
                    '&:hover': { backgroundColor: 'transparent', color: isMicActive ? '#78c072' : '#9797a4' },
                  }),
                  ...(hasMultipleMics && {
                    '&:hover': {
                      ...(TOOLBAR_STYLES.iconButton['&:hover'] ?? {}),
                      color: isMicActive ? '#65ad5f' : '#6e7684',
                    },
                  }),
                }}
              >
                {isSwitchingMic ? (
                  <CircularProgress size={18} sx={{ color: '#9797a4' }} />
                ) : isMicActive ? (
                  <MicSvg />
                ) : (
                  <MicOffSvg />
                )}
              </IconButton>
            </span>
          </Tooltip>

          {(cameras?.length ?? 0) > 0 && (
            <Tooltip title={hasMultipleCameras ? 'Select Camera' : 'Camera'}>
              <span>
                <IconButton
                  onClick={hasMultipleCameras ? handleCameraMenuOpen : undefined}
                  disabled={disabled || isSwitchingCamera || (hasMultipleCameras && isRecording)}
                  aria-label="select camera"
                  size="small"
                  disableRipple={!hasMultipleCameras}
                  sx={{
                    ...TOOLBAR_STYLES.iconButton,
                    color: isCameraActive ? '#78c072' : '#9797a4',
                    ...(!hasMultipleCameras && {
                      cursor: 'default',
                      '&:hover': { backgroundColor: 'transparent', color: isCameraActive ? '#78c072' : '#9797a4' },
                    }),
                  }}
                >
                  {isSwitchingCamera ? <CircularProgress size={18} sx={{ color: '#9797a4' }} /> : <CameraSvg />}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Camera popover menu */}
      <Popover
        open={isCameraMenuOpen}
        anchorEl={cameraMenuAnchor}
        onClose={handleCameraMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '15px',
              boxShadow: '0px 4px 20px 0px rgba(129, 129, 129, 0.12)',
              mt: 1,
              minWidth: 220,
              maxWidth: 320,
              maxHeight: 280,
              overflow: 'auto',
            },
          },
        }}
      >
        <List disablePadding sx={{ py: 0.5 }}>
          {cameras?.map((device, index) => {
            const label = getCameraLabel(device, index);
            const isSelected = device.deviceId === selectedCameraId;
            return (
              <ListItemButton
                key={device.deviceId || index}
                onClick={() => handleCameraItemClick(device.deviceId)}
                selected={isSelected}
                sx={{
                  py: 1,
                  px: 2,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(43, 159, 255, 0.08)',
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: 'rgba(43, 159, 255, 0.12)',
                  },
                }}
              >
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: isSelected ? 500 : 400,
                    color: isSelected ? 'primary.main' : 'text.primary',
                    noWrap: true,
                  }}
                />
                {isSelected && <CheckIcon sx={{ fontSize: 18, color: 'primary.main', ml: 1 }} />}
              </ListItemButton>
            );
          })}
        </List>
      </Popover>

      {/* Microphone popover menu */}
      <Popover
        open={isMicMenuOpen}
        anchorEl={micMenuAnchor}
        onClose={handleMicMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '15px',
              boxShadow: '0px 4px 20px 0px rgba(129, 129, 129, 0.12)',
              mt: 1,
              minWidth: 220,
              maxWidth: 320,
              maxHeight: 280,
              overflow: 'auto',
            },
          },
        }}
      >
        <List disablePadding sx={{ py: 0.5 }}>
          {microphones?.map((device, index) => {
            const label = getMicLabel(device, index);
            const isSelected = device.deviceId === selectedMicId;
            return (
              <ListItemButton
                key={device.deviceId || index}
                onClick={() => handleMicItemClick(device.deviceId)}
                selected={isSelected}
                sx={{
                  py: 1,
                  px: 2,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(43, 159, 255, 0.08)',
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: 'rgba(43, 159, 255, 0.12)',
                  },
                }}
              >
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: isSelected ? 500 : 400,
                    color: isSelected ? 'primary.main' : 'text.primary',
                    noWrap: true,
                  }}
                />
                {isSelected && <CheckIcon sx={{ fontSize: 18, color: 'primary.main', ml: 1 }} />}
              </ListItemButton>
            );
          })}
        </List>
      </Popover>
    </>
  );
};

export default RecordingControls;
